import { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Building2, 
  Briefcase,
  Download
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { API_BASE_URL } from '../lib/api';

interface ReportData {
  accountsByStatus: Array<{ status: string; count: number }>;
  dealsByStatus: Array<{ status: string; count: number; amount: number }>;
  monthlyRevenue: Array<{ month: string; amount: number }>;
  topDeals: Array<{ dealName: string; accountName: string; amount: number }>;
}

export function Reports() {
  const [data, setData] = useState<ReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('thisMonth');

  useEffect(() => {
    fetchReportData();
  }, [period]);

  const fetchReportData = async () => {
    setIsLoading(true);
    try {
      // 簡易的なレポートデータを各APIから取得
      const [accountsRes, dealsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/accounts?limit=1000`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/deals?limit=1000`, { credentials: 'include' }),
      ]);

      const accountsData = await accountsRes.json();
      const dealsData = await dealsRes.json();

      const accounts = accountsData.data || [];
      const deals = dealsData.data || [];

      // 企業を状態別に集計
      const accountsByStatus = accounts.reduce((acc: any, a: any) => {
        const status = a.tradeStatus || '未設定';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      // 案件をステータス別に集計
      const dealsByStatus = deals.reduce((acc: any, d: any) => {
        const status = d.dealStatus || '未設定';
        if (!acc[status]) acc[status] = { count: 0, amount: 0 };
        acc[status].count += 1;
        acc[status].amount += parseFloat(d.dealProfit) || 0;
        return acc;
      }, {});

      // トップ案件
      const topDeals = [...deals]
        .filter((d: any) => d.dealProfit)
        .sort((a: any, b: any) => (parseFloat(b.dealProfit) || 0) - (parseFloat(a.dealProfit) || 0))
        .slice(0, 5)
        .map((d: any) => ({
          dealName: d.dealName,
          accountName: d.accountName,
          amount: parseFloat(d.dealProfit) || 0,
        }));

      setData({
        accountsByStatus: Object.entries(accountsByStatus).map(([status, count]) => ({
          status,
          count: count as number,
        })),
        dealsByStatus: Object.entries(dealsByStatus).map(([status, data]: [string, any]) => ({
          status,
          count: data.count,
          amount: data.amount,
        })),
        monthlyRevenue: [], // 簡易版では省略
        topDeals,
      });
    } catch (err) {
      console.error('Report fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { 
      style: 'currency', 
      currency: 'JPY',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      '取引中': 'bg-green-500',
      '見込み': 'bg-blue-500',
      '休眠': 'bg-gray-400',
      '失注': 'bg-red-500',
      '提案中': 'bg-purple-500',
      '交渉中': 'bg-yellow-500',
      '受注': 'bg-green-500',
      '保留': 'bg-gray-400',
    };
    return colors[status] || 'bg-gray-400';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            レポート
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            営業活動の分析・集計
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 border rounded-lg"
            style={{
              backgroundColor: 'var(--background-card)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="thisMonth">今月</option>
            <option value="lastMonth">先月</option>
            <option value="thisQuarter">今四半期</option>
            <option value="thisYear">今年</option>
          </select>
          <Button variant="outline" leftIcon={<Download size={16} />}>
            レポート出力
          </Button>
        </div>
      </div>

      {/* サマリーカード */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Building2 className="text-blue-600" size={20} />
            </div>
            <div className="text-left">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>企業数</p>
              <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {data?.accountsByStatus.reduce((sum, a) => sum + a.count, 0) || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Briefcase className="text-purple-600" size={20} />
            </div>
            <div className="text-left">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>案件数</p>
              <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {data?.dealsByStatus.reduce((sum, d) => sum + d.count, 0) || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <TrendingUp className="text-green-600" size={20} />
            </div>
            <div className="text-left">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>総利益</p>
              <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {formatCurrency(data?.dealsByStatus.reduce((sum, d) => sum + d.amount, 0) || 0)}
              </p>
            </div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <BarChart3 className="text-orange-600" size={20} />
            </div>
            <div className="text-left">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>受注案件</p>
              <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {data?.dealsByStatus.find(d => d.status === '受注')?.count || 0}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 企業状態別 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 size={20} />
              企業状態別
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.accountsByStatus.map((item) => (
                <div key={item.status} className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(item.status)}`} />
                  <span className="flex-1" style={{ color: 'var(--text-primary)' }}>
                    {item.status}
                  </span>
                  <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 案件ステータス別 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase size={20} />
              案件ステータス別
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.dealsByStatus.map((item) => (
                <div key={item.status} className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(item.status)}`} />
                  <span className="flex-1" style={{ color: 'var(--text-primary)' }}>
                    {item.status}
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {item.count}件
                  </span>
                  <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {formatCurrency(item.amount)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* トップ案件 */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp size={20} />
              トップ案件（利益順）
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.topDeals.length === 0 ? (
              <p className="text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                データがありません
              </p>
            ) : (
              <div className="space-y-3">
                {data?.topDeals.map((deal, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-4 p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--light-gray-primary)' }}
                  >
                    <span 
                      className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white"
                      style={{ backgroundColor: index < 3 ? '#3b82f6' : '#9ca3af' }}
                    >
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {deal.dealName}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {deal.accountName}
                      </p>
                    </div>
                    <span className="text-lg font-bold text-green-600">
                      {formatCurrency(deal.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
